import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Modal from '@/components/modal/Modal';
import { Col, getItemWithExpiration, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { colors } from '@/lib/colors';
import { ToggleSwitch } from '../Settlement/ModalCommission';
import { Toggle } from '@/components/Toggle';
import { createColumnHelper } from '@tanstack/react-table';
import useSingleQuery from '@/hook/useSingleQuery';
import { fetchSessionReviewPermission } from '@/services/useCenterQueryService';
import dayjs from 'dayjs';
import Table from '@/components/Table/Table';
import SvgIcon from '@/components/SvgIcon';
import useCenterMutationService from '@/services/useCenterMutationService';
import { useQueryClient } from '@tanstack/react-query';
import { TooltipWrapper } from '@/components/TooltipWrapper';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
};

type Permission = {
    //  "coachId": "63f1a1b2c3d4e5f678901234",
    // "isReviewPermission": true
    coachId: string;
    coachName: string;
    isReviewPermission: boolean;
};

const columnHelper = createColumnHelper<Permission>();

const ModalCenterReportPermission = ({ setIsVisible }: Props) => {
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const queryClient = useQueryClient();

    const [permissions, setPermissions] = useState<Permission[]>([]);

    const columns = [
        columnHelper.accessor('coachName', {
            id: 'coachName',
            header: '강사',
            cell: (info) => info.getValue(),
            size: 100,
            enableSorting: false,
        }),
        columnHelper.accessor('isReviewPermission', {
            id: 'isReviewPermission',
            header: () => {
                return (
                    <TooltipWrapper
                        tooltip={{
                            customContent: (
                                <Col className="flex w-full h-full items-start justify-center">
                                    <DemiLightText
                                        fontSize={13}
                                        lineHeight={18}
                                        color={'white'}
                                        className="mb-[16px]"
                                        letterSpacing={-0.5}
                                    >
                                        {'* 해당 강사의 '}
                                        <MediumText fontSize={13} lineHeight={18} color={'white'} letterSpacing={-0.5}>
                                            회원이 수업 리뷰를 할 수 있도록 설정합니다.
                                        </MediumText>
                                    </DemiLightText>
                                    <DemiLightText fontSize={13} lineHeight={18} color={'white'} letterSpacing={-0.5}>
                                        * 권한이 꺼지면 회원은 수업 리뷰를 할 수 없습니다.
                                    </DemiLightText>
                                </Col>
                            ),
                            px: 16,
                            py: 20,
                        }}
                    >
                        <Row className="flex items-center gap-[10px]">
                            <RegularText fontSize={16} lineHeight={21} color={'#5D6166'} letterSpacing={-0.5}>
                                수업 리뷰 작성 권한
                            </RegularText>
                            <SvgIcon name="SvgInfoInCircle" size={16} fill={'#B4BCC4'} />
                        </Row>
                    </TooltipWrapper>
                );
            },
            cell: (info) => {
                const coachId = info.row.original.coachId;
                const currentValue = info.getValue();

                const handleToggle = (newValue: boolean) => {
                    setPermissions((prev) => {
                        const updated = prev.map((p) => (p.coachId === coachId ? { ...p, isReviewPermission: newValue } : p));
                        // 개별 토글 변경 시 전체 설정 상태 업데이트
                        const allEnabled = updated.every((p) => p.isReviewPermission);
                        setIsAllSetting(allEnabled);
                        return updated;
                    });
                };

                return <Toggle value={currentValue} width={51} height={26} onToggle={handleToggle} />;
            },
            size: 200,
            enableSorting: false,
        }),
    ];
    // 전체설정
    const [isAllSetting, setIsAllSetting] = useState(false);
    const { refinedResult } = useSingleQuery({
        fetchFn: fetchSessionReviewPermission,
        params: { centerId: authObject?.centerId },
        enabled: !!authObject,
    });
    const sessionReviewPermissionList = (refinedResult as any).data.reviewPermissions;

    useEffect(() => {
        if (sessionReviewPermissionList) {
            setPermissions(sessionReviewPermissionList);
            // 모든 권한이 true인지 확인하여 isAllSetting 업데이트
            const allEnabled = sessionReviewPermissionList.every((p: Permission) => p.isReviewPermission);
            setIsAllSetting(allEnabled);
        }
    }, [sessionReviewPermissionList]);

    const { patchSessionReviewPermissionMutation } = useCenterMutationService({});

    const handleSave = async () => {
        const payload = permissions.map(({ coachId, isReviewPermission }) => ({
            coachId,
            isReviewPermission,
        }));
        const result = await patchSessionReviewPermissionMutation.mutateAsync({
            centerId: authObject?.centerId,
            permissions: payload,
        });
        if (result.status === 200) {
            setIsVisible(false);
            queryClient.invalidateQueries(['getSessionReviewPermissionQuery']);
        } else {
            console.error('Failed to update session review permissions:', result);
        }
    };

    return (
        <Modal setIsVisible={setIsVisible} whiteViewWidth={576} whiteViewHeight={628} borderRadius={25} isCloseButton>
            <Col className="pt-[36px] px-[36px]">
                <Row className="flex w-full items-center mb-[35px] ">
                    <MediumText
                        fontSize={25}
                        lineHeight={30}
                        color={colors.Text_1}
                        className="mr-[58px]"
                        letterSpacing={-0.5}
                    >
                        강사별 수업 리뷰 권한 설정
                    </MediumText>
                    <Row className="flex items-center">
                        <RegularText
                            fontSize={16}
                            lineHeight={21}
                            color={colors.Text_1}
                            className="mr-[8px]"
                            letterSpacing={-0.5}
                        >
                            전체 설정
                        </RegularText>
                        <Toggle
                            width={51}
                            height={26}
                            value={isAllSetting}
                            onToggle={(value) => {
                                setIsAllSetting(value);
                                setPermissions((prev) =>
                                    prev.map((p) => ({
                                        ...p,
                                        isReviewPermission: value,
                                    }))
                                );
                            }}
                        />
                    </Row>
                </Row>
                <Table data={permissions} columns={columns} height={400} headerColor={'#F4F5F7'} isRowBorder />
                <Row className="flex justify-end mt-[35px]">
                    <div
                        className="flex justify-center items-center bg-[#F4F5F7] w-[150px] h-[51px] rounded-[10px] cursor-pointer"
                        onClick={() => setIsVisible(false)}
                    >
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>
                            취소
                        </RegularText>
                    </div>
                    <div
                        className="flex justify-center items-center bg-primary w-[150px] h-[51px] rounded-[10px] cursor-pointer ml-[16px]"
                        onClick={handleSave}
                    >
                        <RegularText fontSize={16} lineHeight={21} color={colors.white}>
                            적용하기
                        </RegularText>
                    </div>
                </Row>
            </Col>
        </Modal>
    );
};

export default ModalCenterReportPermission;
